<script setup>
  import { ref } from 'vue';
  import router from '@/router';
  import { useMutation } from '@tanstack/vue-query';
  import { API } from '@/services/index';
  import { LOGIN_USER_MUTATION, INVITE_CODE_STATUS_MUTATION } from '@/services/auth/mutations/index.js';
  import Cookies from 'js-cookie';
  import AuthFormLayout from '@/components/auth/authFormLayout.vue';
  import partnrUpLogo from '@/assets/logo/PartnrUP_Full.svg';
  import SocialLoginButtons from '@/components/auth/socialLoginButtons.vue';
  import { useRoute, RouterLink } from 'vue-router';
  import { useAuthStore } from '@/stores/auth.store.js';

  const isFormValid = ref(false);
  const loginForm = ref();
  const email = ref(null);
  const password = ref(null);
  const showPassword = ref(false);
  const isLoading = ref(false);
  const isLoadingInvite = ref(false);
  const loginErrors = ref(null);
  const route = useRoute();
  const redirectUrl = ref(route.query?.url ?? '/dashboard');
  const token = route.query?.token ?? null;
  const inviteData = ref(null);
  const signupLink = ref('/signup');

  const formRules = {
    email: (value) => !!value || 'Please enter your email',
    password: (value) => !!value || 'Password is required',
  };

  const onSubmitHandler = () => {
    isLoading.value = true;
    const payload = {
      email: email.value,
      password: password.value,
      invite_token: token,
    };
    loginUserMutation.mutateAsync(payload);
  };

  const loginUserMutation = useMutation({
    mutationFn: async (payload) => {
      return await API.sendQuery('/graphql', LOGIN_USER_MUTATION, payload);
    },
    onSuccess: (data) => {
      if (data.data.data.loginUser) {
        useAuthStore().login(data.data.data.loginUser.token);
        //Cookies.set('_pup_access_token', data.data.data.loginUser.token, {
        //  expires: 7,
        //  sameSite: 'strict',
        //  secure: true
        //});
        router.push(redirectUrl.value);
      }
      if (data.data.errors) {
        let message;
        if (data.data.errors[0].extensions.validation.email?.[0] === 'These credentials do not match our records.') {
          message = 'Incorrect email or password';
        } else if (data.data.errors[0].extensions.validation.invite_token?.[0] === 'Your invite token is not valid for this user.') {
          message = 'Your invite token is not valid for this user.';
        } else {
          message = 'System error. Please try again later.';
        }
        loginErrors.value = message;
        isLoading.value = false;
      }
    },
    onError: (error) => {
      console.log('error', error);
      loginErrors.value = 'System error. Please try again later.';
      isLoading.value = false;
    },
  });

  const inviteCodeStatusMutation = useMutation({
    mutationFn: async (payload) => {
      return await useAuthStore().getInviteData(payload.token);
    },
    onSuccess: (result) => {
      console.log('result', result);
      if (useAuthStore().inviteData) {
        if(useAuthStore().inviteData.new_user == true)
        {
          console.log('here');
          router.push({ path: '/signup', query: { token: token } });
        }

        inviteData.value = useAuthStore().inviteData;
        console.log('Invite data:', inviteData.value, useAuthStore().errors);
        redirectUrl.value = inviteData.value.url;
        isLoadingInvite.value = useAuthStore().isLoadingInvite;
        loginErrors.value = useAuthStore().errors && useAuthStore().errors.length ? useAuthStore().errors[0].message : null;
      }
    },
    onError: (error) => {
      console.log('error', error);
      isLoadingInvite.value = false;
    },
  });

  onBeforeMount(() => {
    if (token) {
      signupLink.value = '/signup?token=' + token;
      inviteData.value = { loading: true };
      isLoadingInvite.value = true;
      inviteData.value = inviteCodeStatusMutation.mutateAsync({ token: token });
    }
  });
</script>

<template>
  <AuthFormLayout :inviteData="inviteData" :is-loading-invite="isLoadingInvite">
    <div class="tw-h-full tw-w-full tw-p-4">
      <div class="tw-relative">
        <img class="tw-mx-auto tw-w-[142px]" :src="partnrUpLogo" alt="PartnrUp" />
        <h3 class="tw-text-center tw-font-mono tw-text-lg tw-font-bold tw-text-primary-blue">Sign in</h3>
        <div class="tw-mt-10">
          <div class="tw-mb-3 tw-mt-8">
            <!-- PUP-265 - disabled-->
            <!--  <SocialLoginButtons />-->
          </div>

          <!--          <div class="tw-my-8 tw-flex tw-justify-center">-->
          <!--            <v-divider color="primary-black" class="border-opacity-100" thickness="1"></v-divider>-->
          <!--            <div class="tw-relative tw-top-[-7px] tw-mx-4 tw-text-[12px] tw-font-bold tw-text-primary-blue">OR</div>-->
          <!--            <v-divider color="primary-black" class="border-opacity-100" thickness="1"></v-divider>-->
          <!--          </div>-->
          <!-- <template v-if="Object.keys(errors).length > 0">
            <ul class="tw-mb-3">
              <li class="tw-mb-1 text-[0.8rem] tw-text-red-500" v-for="(error, index) in errors" :key="index">
                {{ error }}
              </li>
            </ul>
          </template> -->
          <p v-if="loginErrors" class="tw-mb-1 tw-text-center tw-text-[0.8rem] tw-text-red-500">
            {{ loginErrors }}
          </p>
          <v-form ref="loginForm" v-model="isFormValid" @submit.prevent="onSubmitHandler">
            <div class="tw-flex tw-w-full tw-flex-col tw-space-y-3">
              <div class="tw-flex tw-w-full tw-flex-col">
                <v-text-field
                  v-model="email"
                  variant="outlined"
                  color="primary-blue"
                  placeholder="Email Address*"
                  :rules="[formRules.email]"
                  hide-details="auto"
                  :disabled="isLoading || isLoadingInvite"
                  density="comfortable"
                >
                </v-text-field>
              </div>
              <div class="tw-flex tw-w-full tw-flex-col">
                <v-text-field
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  variant="outlined"
                  color="primary-blue"
                  placeholder="Password*"
                  :rules="[formRules.password]"
                  :disabled="isLoading || isLoadingInvite"
                  density="comfortable"
                >
                  <template v-slot:append-inner>
                    <v-icon color="primary-blue" @click="showPassword = !showPassword" :style="password ? 'opacity: 100%' : ''">
                      {{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}
                    </v-icon>
                  </template>
                </v-text-field>
              </div>
            </div>
            <div class="tw-mt-3 tw-w-full tw-text-center tw-text-[0.75rem] tw-font-semibold tw-text-primary-blue">
              <RouterLink :to="'/reset'+(token ? '?code=' + token : '')">Forgot Password?</RouterLink>
            </div>
            <div class="tw-mt-9 tw-flex tw-w-full">
              <v-btn
                color="primary-blue"
                size="large"
                rounded="lg"
                block
                flat
                class="tw-font-bold tw-capitalize"
                style="letter-spacing: 0px"
                type="submit"
                :loading="isLoading"
                :disabled="isLoading || !isFormValid || isLoadingInvite"
              >
                Sign In
              </v-btn>
            </div>
          </v-form>
          <div class="tw-mt-3 tw-w-full tw-text-center tw-text-[0.813rem] tw-font-semibold tw-text-primary-blue">
            <span class="tw-font-normal">Don't have an account?</span>
            <RouterLink class="tw-ml-1 tw-text-[0.813rem] tw-font-bold" :to="signupLink"> Create one </RouterLink>
          </div>
        </div>
      </div>
    </div>
  </AuthFormLayout>
</template>

<style scoped></style>
